import React, { useState, useEffect, useCallback, useRef } from 'react';
import onboardingConfig from '../../onboarding.config.json';

// Toggle verbose console logging for onboarding hints
const ONBOARDING_DEBUG = false;
const dlog = (...args) => {
  if (ONBOARDING_DEBUG) console.log(...args);
};

const OnboardingHints = ({ windowName = "main", permanentMode = false, activeTab = null }) => {
  const [windowHintsEnabled, setWindowHintsEnabled] = useState({});
  const [dismissedHints, setDismissedHints] = useState([]);
  const [activeHint, setActiveHint] = useState(null);
  const [mountedHints, setMountedHints] = useState([]);
  const [currentHintIndex, setCurrentHintIndex] = useState(0);
  const [isPermanentMode] = useState(permanentMode || onboardingConfig.permanentMode);
  const portalRef = useRef(null);
  const resizeTimeoutRef = useRef(null);

  // Initialize state from localStorage
  useEffect(() => {
    const storedVersion = localStorage.getItem('onboarding.version');
    const storedWindowStates = JSON.parse(localStorage.getItem('onboarding.windowStates') || '{}');
    const storedHidden = JSON.parse(localStorage.getItem('onboarding.hidden') || '[]');

    // Version check - reset if version changed
    if (storedVersion !== onboardingConfig.version) {
      localStorage.setItem('onboarding.version', onboardingConfig.version);
      const initialStates = { [windowName]: false };
      localStorage.setItem('onboarding.windowStates', JSON.stringify(initialStates));
      localStorage.setItem('onboarding.hidden', '[]');
      setWindowHintsEnabled(initialStates);
      setDismissedHints([]);
    } else {
      // Initialize current window to false if not set (hints disabled by default)
      if (storedWindowStates[windowName] === undefined) {
        storedWindowStates[windowName] = false;
        localStorage.setItem('onboarding.windowStates', JSON.stringify(storedWindowStates));
      }
      setWindowHintsEnabled(storedWindowStates);
      setDismissedHints(storedHidden);
    }
  }, [windowName]);

  // Listen for onboarding toggle events from help button
  useEffect(() => {
    const handleToggleEvent = (event) => {
      if (event.detail.windowName === windowName) {
        dlog(`🎛️ Received toggle event for ${windowName}:`, event.detail.enabled);
        const newWindowStates = { ...windowHintsEnabled, [windowName]: event.detail.enabled };
        setWindowHintsEnabled(newWindowStates);

        if (event.detail.enabled) {
          // When enabling, reset dismissed hints and start from first hint
          setDismissedHints([]);
          setCurrentHintIndex(0);
        } else {
          // When disabling, clear mounted hints
          setMountedHints([]);
          setActiveHint(null);
          setCurrentHintIndex(0);
        }
      }
    };

    window.addEventListener('onboardingToggle', handleToggleEvent);
    return () => window.removeEventListener('onboardingToggle', handleToggleEvent);
  }, [windowName, windowHintsEnabled]);

  // Create portal container
  useEffect(() => {
    let portal = document.getElementById('onboard-portal');
    if (!portal) {
      portal = document.createElement('div');
      portal.id = 'onboard-portal';
      portal.style.position = 'fixed';
      portal.style.top = '0';
      portal.style.left = '0';
      portal.style.pointerEvents = 'none';
      portal.style.zIndex = '10000';
      document.body.appendChild(portal);
    }
    portalRef.current = portal;

    return () => {
      if (portal && portal.parentNode) {
        portal.parentNode.removeChild(portal);
      }
    };
  }, []);

  // Find visible hint targets for current window
  const findVisibleTargets = useCallback(() => {
    const currentWindowHints = onboardingConfig.hints.filter(hint =>
      hint.window === windowName &&
      (!hint.tab || hint.tab === activeTab) &&
      !dismissedHints.includes(hint.id)
    );

    dlog(`🎯 Finding targets for ${windowName} (tab: ${activeTab}):`, {
      totalHints: onboardingConfig.hints.length,
      currentWindowHints: currentWindowHints.length,
      dismissed: dismissedHints
    });

    const visibleHints = [];

    currentWindowHints.forEach(hint => {
      const elements = document.querySelectorAll(hint.selector);
      dlog(`🔍 Selector "${hint.selector}" found ${elements.length} elements for hint "${hint.id}"`);

      elements.forEach(element => {
        const rect = element.getBoundingClientRect();
        const isVisible = rect.width > 0 && rect.height > 0 &&
                         rect.top >= 0 && rect.left >= 0 &&
                         rect.bottom <= window.innerHeight &&
                         rect.right <= window.innerWidth;

        dlog(`📐 Element for ${hint.id}: visible=${isVisible}, rect:`, rect);

        if (isVisible) {
          visibleHints.push({ ...hint, element });
        }
      });
    });

    return visibleHints.sort((a, b) => (a.priority || 999) - (b.priority || 999));
  }, [windowName, activeTab, dismissedHints]);

  // Calculate hint position with improved stability
  const calculatePosition = useCallback((hint, element) => {
    const rect = element.getBoundingClientRect();
    const offset = hint.offset || 10;
    const tooltipWidth = 280;
    const tooltipHeight = 120;

    let finalPlacement = hint.placement || 'auto';
    let position = { top: 0, left: 0, transform: 'translate(0, 0)' };

    // If auto, choose best placement based on available space
    if (finalPlacement === 'auto') {
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceLeft = rect.left;
      const spaceRight = window.innerWidth - rect.right;

      if (spaceBelow >= tooltipHeight + offset) {
        finalPlacement = 'bottom';
      } else if (spaceAbove >= tooltipHeight + offset) {
        finalPlacement = 'top';
      } else if (spaceRight >= tooltipWidth + offset) {
        finalPlacement = 'right';
      } else if (spaceLeft >= tooltipWidth + offset) {
        finalPlacement = 'left';
      } else {
        finalPlacement = 'bottom';
      }
    }

    // Calculate position based on determined placement
    switch (finalPlacement) {
      case 'top':
        position = {
          top: rect.top - tooltipHeight - offset,
          left: Math.max(10, Math.min(rect.left + rect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 10)),
          transform: 'translate(0, 0)'
        };
        break;
      case 'bottom':
        position = {
          top: rect.bottom + offset,
          left: Math.max(10, Math.min(rect.left + rect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 10)),
          transform: 'translate(0, 0)'
        };
        break;
      case 'left':
        position = {
          top: Math.max(10, Math.min(rect.top + rect.height / 2 - tooltipHeight / 2, window.innerHeight - tooltipHeight - 10)),
          left: rect.left - tooltipWidth - offset,
          transform: 'translate(0, 0)'
        };
        break;
      case 'right':
        position = {
          top: Math.max(10, Math.min(rect.top + rect.height / 2 - tooltipHeight / 2, window.innerHeight - tooltipHeight - 10)),
          left: rect.right + offset,
          transform: 'translate(0, 0)'
        };
        break;
      default:
        position = {
          top: rect.bottom + offset,
          left: Math.max(10, Math.min(rect.left + rect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 10)),
          transform: 'translate(0, 0)'
        };
        break;
    }

    // Ensure tooltip stays within viewport bounds
    position.top = Math.max(10, Math.min(position.top, window.innerHeight - tooltipHeight - 10));
    position.left = Math.max(10, Math.min(position.left, window.innerWidth - tooltipWidth - 10));

    return { ...position, placement: finalPlacement };
  }, []);

  // Render hint tooltip
  const renderHint = useCallback((hint, position, hintIndex, totalHints) => {
    if (!portalRef.current) return null;

    const isLastHint = hintIndex === totalHints - 1;

    const handleSkip = (e) => {
      e.stopPropagation();
      // Dismiss all hints and exit tour
      const visibleHints = findVisibleTargets();
      const allHintIds = visibleHints.map(h => h.id);
      setDismissedHints(allHintIds);
      localStorage.setItem('onboarding.hidden', JSON.stringify(allHintIds));
      setCurrentHintIndex(0);
      setMountedHints([]);
      setActiveHint(null);

      // Update window state to disabled and notify button
      setWindowHintsEnabled(prev => {
        const newWindowStates = { ...prev, [windowName]: false };
        localStorage.setItem('onboarding.windowStates', JSON.stringify(newWindowStates));

        // Notify help button that hints are now disabled
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('onboardingToggle', {
            detail: { windowName, enabled: false }
          }));
        }, 0);

        return newWindowStates;
      });
    };

    const handleNext = (e) => {
      e.stopPropagation();
      if (isLastHint) {
        // Close tour on last hint
        handleSkip(e);
      } else {
        // Move to next hint
        setCurrentHintIndex(hintIndex + 1);
      }
    };

    const handleDismiss = (e) => {
      e.stopPropagation();
      const newDismissed = [...dismissedHints, hint.id];
      setDismissedHints(newDismissed);
      localStorage.setItem('onboarding.hidden', JSON.stringify(newDismissed));
      setActiveHint(null);

      // Move to next hint or close if last
      if (!isLastHint) {
        setCurrentHintIndex(hintIndex + 1);
      } else {
        setCurrentHintIndex(0);
        setMountedHints([]);
      }
    };

    const tooltip = (
      <div
        key={hint.id}
        className="onboard-tooltip bg-white rounded-xl shadow-xs border border-gray-300 px-4 py-3 md:px-6 md:py-4"
        data-placement={position.placement}
        style={{
          position: 'absolute',
          top: position.top,
          left: position.left,
          transform: position.transform,
          pointerEvents: 'auto',
          maxWidth: '280px'
        }}
      >
        <div className="onboard-content">
          <div className="onboard-header flex items-start justify-between mb-2">
            <h4 className="paragraph_large_bold text-gray-700 flex-1 mr-4">{hint.title}</h4>
            {hint.dismissible && (
              <button
                className="onboard-close flex items-center justify-center flex-shrink-0 p-1 -mt-1"
                onClick={handleDismiss}
                aria-label="Close hint"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="flex-shrink-0">
                  <path d="M1 1L13 13M1 13L13 1L1 13Z" stroke="#5A5A5A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>
          <p className="paragraph_normal_regular text-gray-700 mb-4">{hint.body}</p>
          <div className="onboard-actions flex justify-end">
            <button
              className="onboard-button onboard-next button_large_bold rounded-full bg-brand-orange-600 text-white hover:brightness-125 transition duration-200 px-5 py-2 md:px-8 md:py-3"
              onClick={handleNext}
            >
              {isLastHint ? 'Got it' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    );

    return tooltip;
  }, [dismissedHints, findVisibleTargets, windowName]);

  // Show hints when window hints are enabled
  useEffect(() => {
    const isWindowEnabled = windowHintsEnabled[windowName] === true; // Default to false if not set

    dlog(`🔍 OnboardingHints [${windowName}] (tab: ${activeTab}):`, {
      isWindowEnabled,
      isPermanentMode,
      windowHintsEnabled,
      currentWindow: windowName
    });

    if (isWindowEnabled || isPermanentMode) {
      const visibleHints = findVisibleTargets();
      dlog(`👀 Found ${visibleHints.length} visible hints for ${windowName}:`, visibleHints.map(h => h.id));
      setMountedHints(visibleHints);
    } else {
      dlog(`❌ Hints disabled for ${windowName}`);
      setMountedHints([]);
      setActiveHint(null);
    }
  }, [windowHintsEnabled, windowName, activeTab, findVisibleTargets, isPermanentMode]);

  // Handle element interactions
  useEffect(() => {
    const isWindowEnabled = windowHintsEnabled[windowName] === true;

    if (!isWindowEnabled && !isPermanentMode) return;

    const handleMouseEnter = (e) => {
      if (isPermanentMode) return; // Don't handle hover in permanent mode

      const dataOnboard = e.target.getAttribute('data-onboard');
      if (!dataOnboard) return;

      const hint = onboardingConfig.hints.find(h =>
        h.id === dataOnboard && h.window === windowName && !dismissedHints.includes(h.id)
      );

      if (!hint) return;

      if (hint.triggers && hint.triggers.includes('hover')) {
        const position = calculatePosition(hint, e.target);
        setActiveHint({ hint, position });
      }
    };

    const handleMouseLeave = () => {
      if (!isPermanentMode) {
        setActiveHint(null);
      }
    };

    const handleFocus = (e) => {
      if (isPermanentMode) return;

      const dataOnboard = e.target.getAttribute('data-onboard');
      if (!dataOnboard) return;

      const hint = onboardingConfig.hints.find(h =>
        h.id === dataOnboard && h.window === windowName && !dismissedHints.includes(h.id)
      );

      if (!hint) return;

      if (hint.triggers && hint.triggers.includes('focus')) {
        const position = calculatePosition(hint, e.target);
        setActiveHint({ hint, position });
      }
    };

    const handleBlur = () => {
      if (!isPermanentMode) {
        setActiveHint(null);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && activeHint) {
        setActiveHint(null);
      }
    };

    // Add event listeners
    document.addEventListener('mouseover', handleMouseEnter);
    document.addEventListener('mouseout', handleMouseLeave);
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mouseover', handleMouseEnter);
      document.removeEventListener('mouseout', handleMouseLeave);
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [windowName, windowHintsEnabled, isPermanentMode, dismissedHints, calculatePosition, activeHint]);

  // Handle scroll and resize
  useEffect(() => {
    const handleScrollOrResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = setTimeout(() => {
        if (activeHint) {
          const elements = document.querySelectorAll(`[data-onboard="${activeHint.hint.id}"]`);
          if (elements.length > 0) {
            const position = calculatePosition(activeHint.hint, elements[0]);
            setActiveHint(prev => ({ ...prev, position }));
          }
        }

        const isWindowEnabled = windowHintsEnabled[windowName] !== false;
        if (isWindowEnabled || isPermanentMode) {
          const visibleHints = findVisibleTargets();
          setMountedHints(visibleHints);
        }
      }, 100);
    };

    window.addEventListener('scroll', handleScrollOrResize);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [activeHint, windowName, windowHintsEnabled, isPermanentMode, findVisibleTargets, calculatePosition]);

  // Render tooltips
  if (!portalRef.current) {
    dlog(`⚠️ No portal container for ${windowName}`);
    return null;
  }

  const tooltipsToRender = [];
  const isWindowEnabled = windowHintsEnabled[windowName] !== false;

  dlog(`🎨 Rendering tooltips for ${windowName}:`, {
    isWindowEnabled,
    isPermanentMode,
    mountedHints: mountedHints.length,
    activeHint: activeHint ? activeHint.hint.id : null
  });

  // Show current hint only (sequential mode)
  if ((isPermanentMode || isWindowEnabled) && mountedHints.length > 0) {
    // Only show one hint at a time based on currentHintIndex
    const currentHint = mountedHints[currentHintIndex];
    if (currentHint) {
      dlog(`✅ Adding hint ${currentHintIndex + 1}/${mountedHints.length}: ${currentHint.id} to render`);
      const position = calculatePosition(currentHint, currentHint.element);
      tooltipsToRender.push(renderHint(currentHint, position, currentHintIndex, mountedHints.length));
    }
  }

  // Show active hint on hover (only if not in permanent mode)
  if (!isPermanentMode && isWindowEnabled && activeHint && !mountedHints.find(h => h.id === activeHint.hint.id)) {
    dlog(`✅ Adding active hint ${activeHint.hint.id} to render`);
    tooltipsToRender.push(renderHint(activeHint.hint, activeHint.position, 0, 1));
  }

  dlog(`🚀 Final tooltips to render: ${tooltipsToRender.length}`);

  return (
    <>
      {tooltipsToRender.map((tooltip, index) => (
        <div key={index}>{tooltip}</div>
      ))}
    </>
  );
};

export default OnboardingHints;
